import { LegacyChainClient } from '../core/LegacyChainClient';
import { PlanType } from '../types';

export interface SupportTicket {
  id: string;
  subject: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  category: 'technical' | 'billing' | 'feature_request' | 'bug' | 'other';
  createdAt: Date;
  updatedAt: Date;
  resolvedAt?: Date;
  assignee?: string;
  messages: SupportMessage[];
}

export interface SupportMessage {
  id: string;
  ticketId: string;
  sender: 'user' | 'support';
  message: string;
  attachments?: string[];
  createdAt: Date;
}

export interface SLAMetrics {
  responseTime: {
    target: number; // hours
    current: number;
    withinSLA: boolean;
  };
  resolutionTime: {
    target: number; // hours
    average: number;
    withinSLA: boolean;
  };
  satisfaction: {
    score: number; // 1-5
    responses: number;
  };
}

export class SupportService {
  constructor(private client: LegacyChainClient) {}

  /**
   * Get support priority based on plan
   */
  private getSupportPriority(): 'standard' | 'priority' | 'dedicated' {
    const planType = this.client.getPlanType();
    switch (planType) {
      case PlanType.ENTERPRISE:
        return 'dedicated';
      case PlanType.PRO:
        return 'priority';
      default:
        return 'standard';
    }
  }

  /**
   * Create a support ticket
   */
  async createTicket(input: {
    subject: string;
    description: string;
    category: SupportTicket['category'];
    attachments?: File[];
  }): Promise<SupportTicket> {
    const supportLevel = this.getSupportPriority();
    
    // Auto-assign priority based on plan
    let priority: SupportTicket['priority'] = 'medium';
    if (supportLevel === 'dedicated') {
      priority = 'high';
    } else if (supportLevel === 'priority') {
      priority = 'medium';
    } else {
      priority = 'low';
    }

    const formData = new FormData();
    formData.append('subject', input.subject);
    formData.append('description', input.description);
    formData.append('category', input.category);
    formData.append('priority', priority);
    
    if (input.attachments) {
      input.attachments.forEach((file, index) => {
        formData.append(`attachments[${index}]`, file);
      });
    }

    const response = await this.client.makeRequest<SupportTicket>(
      'POST',
      '/support/tickets',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to create ticket');
    }

    return response.data;
  }

  /**
   * Get support tickets
   */
  async getTickets(options?: {
    status?: SupportTicket['status'];
    limit?: number;
    offset?: number;
  }): Promise<{
    tickets: SupportTicket[];
    total: number;
  }> {
    const params = new URLSearchParams();
    if (options?.status) params.append('status', options.status);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());

    const response = await this.client.makeRequest<any>(
      'GET',
      `/support/tickets?${params.toString()}`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to get tickets');
    }

    return response.data;
  }

  /**
   * Get a specific ticket
   */
  async getTicket(ticketId: string): Promise<SupportTicket> {
    const response = await this.client.makeRequest<SupportTicket>(
      'GET',
      `/support/tickets/${ticketId}`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to get ticket');
    }

    return response.data;
  }

  /**
   * Reply to a ticket
   */
  async replyToTicket(
    ticketId: string,
    message: string,
    attachments?: File[]
  ): Promise<SupportMessage> {
    const formData = new FormData();
    formData.append('message', message);
    
    if (attachments) {
      attachments.forEach((file, index) => {
        formData.append(`attachments[${index}]`, file);
      });
    }

    const response = await this.client.makeRequest<SupportMessage>(
      'POST',
      `/support/tickets/${ticketId}/messages`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to send message');
    }

    return response.data;
  }

  /**
   * Close a ticket
   */
  async closeTicket(ticketId: string): Promise<boolean> {
    const response = await this.client.makeRequest<{ success: boolean }>(
      'PUT',
      `/support/tickets/${ticketId}/close`
    );

    return response.success;
  }

  /**
   * Rate support experience
   */
  async rateSupport(
    ticketId: string,
    rating: 1 | 2 | 3 | 4 | 5,
    feedback?: string
  ): Promise<boolean> {
    const response = await this.client.makeRequest<{ success: boolean }>(
      'POST',
      `/support/tickets/${ticketId}/rate`,
      { rating, feedback }
    );

    return response.success;
  }

  /**
   * Get SLA metrics (Enterprise only)
   */
  async getSLAMetrics(): Promise<SLAMetrics> {
    const planType = this.client.getPlanType();
    if (planType !== PlanType.ENTERPRISE) {
      throw new Error('SLA metrics are only available for Enterprise plans');
    }

    const response = await this.client.makeRequest<SLAMetrics>(
      'GET',
      '/support/sla-metrics'
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to get SLA metrics');
    }

    return response.data;
  }

  /**
   * Request dedicated support session (Enterprise only)
   */
  async requestDedicatedSession(input: {
    topic: string;
    preferredTime: Date;
    duration: 30 | 60; // minutes
    type: 'call' | 'screen_share' | 'in_person';
  }): Promise<{
    sessionId: string;
    scheduledTime: Date;
    meetingLink?: string;
  }> {
    const planType = this.client.getPlanType();
    if (planType !== PlanType.ENTERPRISE) {
      throw new Error('Dedicated support sessions are only available for Enterprise plans');
    }

    const response = await this.client.makeRequest<any>(
      'POST',
      '/support/dedicated-session',
      input
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to schedule session');
    }

    return response.data;
  }

  /**
   * Get support availability
   */
  async getAvailability(): Promise<{
    isAvailable: boolean;
    nextAvailable?: Date;
    averageResponseTime: number; // minutes
  }> {
    const response = await this.client.makeRequest<any>(
      'GET',
      '/support/availability'
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to get availability');
    }

    return response.data;
  }

  /**
   * Access knowledge base
   */
  async searchKnowledgeBase(query: string): Promise<Array<{
    id: string;
    title: string;
    excerpt: string;
    category: string;
    url: string;
    helpful: number;
    notHelpful: number;
  }>> {
    const params = new URLSearchParams({ q: query });

    const response = await this.client.makeRequest<any>(
      'GET',
      `/support/knowledge-base/search?${params.toString()}`
    );

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Failed to search knowledge base');
    }

    return response.data;
  }
} 